import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Plus, Trash2, Pencil, ArrowLeft, Save, X } from "lucide-react";
import api from "@/lib/api";
import { PageHeader, Card, Button, Input, Select, Field, Modal, Skeleton, Badge } from "@/components/ui-kit";
import { formatIDR } from "@/lib/format";

/**
 * Iter v34b6 — Master Komponen Payroll.
 * Kelola tunjangan (Uang Makan, Bonus, dsb) dan potongan (BPJS, dsb) yang otomatis
 * ditambahkan saat generate batch payroll.
 */
const blank = {
  name: "",
  kind: "earning",           // earning | deduction
  calc_mode: "fixed",         // fixed | per_work_day | per_overtime_hour
  amount: 0,
  enabled_by_default: true,
  description: "",
};

export default function PayrollComponents() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blank);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  const load = () => {
    setLoading(true);
    api.get("/payroll/components").then((r) => setRows(r.data || [])).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const openNew = () => { setForm(blank); setEditingId(null); setOpen(true); };
  const openEdit = (row) => {
    setForm({
      name: row.name || "",
      kind: row.kind || "earning",
      calc_mode: row.calc_mode || "fixed",
      amount: row.amount || 0,
      enabled_by_default: row.enabled_by_default !== false,
      description: row.description || "",
    });
    setEditingId(row.id);
    setOpen(true);
  };
  const del = async (row) => {
    if (!window.confirm(`Hapus komponen "${row.name}"?`)) return;
    try {
      await api.delete(`/payroll/components/${row.id}`);
      toast.success("Komponen dihapus");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal hapus"); }
  };
  const save = async () => {
    if (!form.name.trim()) { toast.error("Nama komponen wajib diisi"); return; }
    setSaving(true);
    try {
      const payload = { ...form, amount: Number(form.amount) || 0 };
      if (editingId) {
        await api.patch(`/payroll/components/${editingId}`, payload);
        toast.success("Komponen diperbarui");
      } else {
        await api.post("/payroll/components", payload);
        toast.success("Komponen ditambahkan");
      }
      setOpen(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal simpan"); }
    finally { setSaving(false); }
  };

  const modeLabel = (m) =>
    m === "per_work_day" ? "× hari kerja"
    : m === "per_overtime_hour" ? "× jam lembur"
    : "Flat";

  return (
    <div className="min-h-full bg-paper p-4 md:p-6 lg:p-8">
      <div className="max-w-[1200px] mx-auto space-y-6">
        <div>
          <button onClick={() => navigate("/payroll")} className="flex items-center gap-2 text-sm text-stone-600 hover:text-stone-900 mb-2" data-testid="components-back-btn">
            <ArrowLeft className="h-4 w-4"/> Kembali ke Payroll
          </button>
        </div>
        <PageHeader
          overline="HR · Payroll"
          title="Master Komponen Gaji"
          description="Kelola tunjangan (Uang Makan, Bonus Kehadiran) dan potongan (BPJS, Iuran) yang otomatis ditambahkan saat generate batch payroll."
          action={<Button onClick={openNew} data-testid="components-new-btn"><Plus className="h-3.5 w-3.5"/> Tambah Komponen</Button>}
        />

        <Card className="p-0">
          {loading ? (
            <div className="p-4 space-y-2">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-14 w-full"/>)}</div>
          ) : rows.length === 0 ? (
            <div className="text-center py-16 text-stone-400" data-testid="components-empty">
              Belum ada komponen. Klik <b>Tambah Komponen</b> untuk membuat komponen master gaji.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="components-table">
                <thead className="bg-stone-50">
                  <tr className="text-left text-[11px] uppercase tracking-widest text-stone-500 border-b">
                    <th className="py-3 px-3">Nama</th>
                    <th className="py-3 px-3">Jenis</th>
                    <th className="py-3 px-3">Cara Hitung</th>
                    <th className="py-3 px-3 text-right">Nominal</th>
                    <th className="py-3 px-3 text-center">Default</th>
                    <th className="py-3 px-3">Deskripsi</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} className="border-b border-stone-100 hover:bg-stone-50/50" data-testid={`components-row-${r.id}`}>
                      <td className="py-3 px-3 font-medium text-stone-900">{r.name}</td>
                      <td className="py-3 px-3">
                        <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded ${r.kind === "earning" ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800"}`}>
                          {r.kind === "earning" ? "Tunjangan" : "Potongan"}
                        </span>
                      </td>
                      <td className="py-3 px-3 text-[11px] text-stone-600">{modeLabel(r.calc_mode)}</td>
                      <td className="py-3 px-3 text-right font-mono-num">{formatIDR(r.amount)}</td>
                      <td className="py-3 px-3 text-center">
                        {r.enabled_by_default ? <Badge tone="emerald">Ya</Badge> : <Badge tone="stone">Tidak</Badge>}
                      </td>
                      <td className="py-3 px-3 text-[11px] text-stone-500 max-w-xs truncate">{r.description || "—"}</td>
                      <td className="py-3 px-3 text-right whitespace-nowrap">
                        <button onClick={() => openEdit(r)} className="text-stone-500 hover:text-blue-700 px-2" data-testid={`components-edit-${r.id}`}><Pencil className="h-3.5 w-3.5"/></button>
                        <button onClick={() => del(r)} className="text-stone-400 hover:text-red-700 px-2" data-testid={`components-del-${r.id}`}><Trash2 className="h-3.5 w-3.5"/></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title={editingId ? "Edit Komponen" : "Tambah Komponen"} size="md">
        <div className="space-y-4" data-testid="components-form">
          <Field label="Nama Komponen">
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="mis: Uang Makan, BPJS Kesehatan" data-testid="comp-name" autoFocus/>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Jenis">
              <Select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} data-testid="comp-kind">
                <option value="earning">Tunjangan (menambah)</option>
                <option value="deduction">Potongan (mengurangi)</option>
              </Select>
            </Field>
            <Field label="Cara Hitung">
              <Select value={form.calc_mode} onChange={(e) => setForm({ ...form, calc_mode: e.target.value })} data-testid="comp-calc-mode">
                <option value="fixed">Flat / Nominal Tetap</option>
                <option value="per_work_day">× Jumlah Hari Kerja</option>
                <option value="per_overtime_hour">× Jumlah Jam Lembur</option>
              </Select>
            </Field>
          </div>
          <Field label={`Nominal ${form.calc_mode === "per_work_day" ? "per hari kerja" : form.calc_mode === "per_overtime_hour" ? "per jam lembur" : ""}`}>
            <Input type="number" min="0" step="1000" value={form.amount} onChange={(e) => setForm({ ...form, amount: Number(e.target.value) || 0 })} className="font-mono-num text-right" data-testid="comp-amount"/>
          </Field>
          <Field label="Deskripsi (opsional)">
            <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Catatan internal" data-testid="comp-description"/>
          </Field>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={form.enabled_by_default} onChange={(e) => setForm({ ...form, enabled_by_default: e.target.checked })} data-testid="comp-enabled-default"/>
            <span>Aktif secara default saat generate batch payroll</span>
          </label>
          <div className="flex justify-end gap-2 pt-3 border-t border-stone-100">
            <Button variant="ghost" onClick={() => setOpen(false)}><X className="h-3.5 w-3.5"/> Batal</Button>
            <Button onClick={save} disabled={saving} data-testid="comp-save-btn">
              <Save className="h-3.5 w-3.5"/> {saving ? "Menyimpan..." : "Simpan"}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
